import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { HomepageContentResponse } from 'src/app/admin/ad-admin/ad-admin.service';
import { VisitorCountryService } from 'src/app/Theme/shared/service/visitor-country.service';

export interface HomepageContentQuery {
  layoutDisplayId?: string | null;
  countryCode?: string | null;
}

/** Ticket origin for POST `/CF/cf_sendTicket`. */
export type CfSendTicketType = 'basket' | 'invoice' | 'homepage';

export interface CfSendTicketBasketItem {
  item: string;
  quantity: number | string;
  price: number | string;
  currency?: string;
}

export interface CfSendTicketBasket {
  name?: string;
  items: CfSendTicketBasketItem[];
}

/** Stable invoice identifiers only — backend resolves the rest. */
export interface CfSendTicketInvoiceRef {
  financial_company: string | number;
  document_number: string | number;
  line?: string | number;
}

/**
 * Structured create body for POST `/CF/cf_sendTicket`.
 * Only real form/context values; omit empty optional fields. Do not send status.
 */
export interface CfSendTicketRequest {
  type: CfSendTicketType;
  email: string;
  name: string;
  description: string;
  company?: string;
  basket?: CfSendTicketBasket;
  invoices?: CfSendTicketInvoiceRef[];
}

@Injectable({
  providedIn: 'root'
})
export class CfHomeService {

  private selectedBestSellerSource = new BehaviorSubject<any | null>(null);
  selectedBestSeller$ = this.selectedBestSellerSource.asObservable();
  private host = environment.apiUrlHost;

  constructor(
    private http: HttpClient,
    private visitorCountry: VisitorCountryService,
  ) { }

  private buildHomepageQueryParams(query?: HomepageContentQuery): HttpParams {
    let params = new HttpParams();
    if (query?.layoutDisplayId) {
      params = params.set('layout_display_id', query.layoutDisplayId);
    }
    if (query?.countryCode) {
      params = params.set('country_code', query.countryCode);
    }
    return params;
  }

  /**
   * Homepage endpoints reject the call unless layout_display_id or country_code is set.
   * Callers that omit the country get it from the visitor IP, same as the desktop homepage.
   */
  private paramsForHomepage(query?: HomepageContentQuery): Observable<HttpParams> {
    const layoutDisplayId = query?.layoutDisplayId?.trim() || null;
    const countryCode = query?.countryCode?.trim() || null;
    if (countryCode || layoutDisplayId) {
      return of(this.buildHomepageQueryParams({ layoutDisplayId, countryCode }));
    }
    return this.visitorCountry.getCountryCode().pipe(
      map((detected) => this.buildHomepageQueryParams({ countryCode: detected })),
    );
  }

  private requestHomepage<T>(
    url: string,
    query: HomepageContentQuery | undefined,
    empty?: T,
  ): Observable<T> {
    return this.paramsForHomepage(query).pipe(
      switchMap((params) => {
        if (!params.has('country_code') && !params.has('layout_display_id')) {
          console.warn('[CfHome] Visitor IP did not resolve a country_code for', url);
          return of(empty as T);
        }
        return this.http.get<T>(url, { params });
      }),
    );
  }

  /**
   * GET /AD/homepage/content/ — all homepage sections for a country or layout.
   * Portal: pass countryCode only. Admin: pass layoutDisplayId.
   */
  public getHomepageContentByCountry(countryCode: string | null): Observable<HomepageContentResponse> {
    return this.getHomepageContent({ countryCode });
  }

  public getHomepageContent(query: HomepageContentQuery = {}): Observable<HomepageContentResponse> {
    return this.requestHomepage<HomepageContentResponse>(
      `${this.host}/AD/homepage/content/`,
      query,
    );
  }

  public SearchProductList(): Observable<any> {
    return this.http.get(`${this.host}/CF/item_by_name/`);
  }

  /** GET /AD/popular/?country_code= — fallback when not using combined content endpoint. */
  public getPopularProductsList(countryCode?: string | null): Observable<any> {
    return this.requestHomepage(`${this.host}/AD/popular/`, { countryCode }, []);
  }

  /** GET /AD/bestsellers/?country_code= */
  public getBestSellersList(countryCode?: string | null): Observable<any> {
    return this.requestHomepage(`${this.host}/AD/bestsellers/`, { countryCode }, []);
  }

  /** GET /AD/topics/?country_code= */
  public getTopicList(countryCode?: string | null): Observable<any> {
    return this.requestHomepage(`${this.host}/AD/topics/`, { countryCode }, []);
  }

  public sendTicket(data: CfSendTicketRequest): Observable<any> {
    return this.http.post<any>(`${this.host}/CF/cf_sendTicket`, data);
  }

  setSelectedBestSeller(bestSeller: any): void {
    this.selectedBestSellerSource.next(bestSeller);
  }

  public getBestSellersListProduct(countryCode?: string | null): Observable<any> {
    return this.getBestSellersList(countryCode);
  }
}
